import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Row, Col, Alert, Button, Spinner, Image, Badge } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { UserInfo } from './AuthLogin';
import { getCastaways } from '../api/castaways';
import { Castaway } from '../types';
import { scoreDraftByLineup, activeCastawayIdsForEpisode } from '../scoring';
import { getTribeColor } from './TribeBadge';

interface HomeProps {
  userInfo: UserInfo;
  userEntries: FoundUser[];
  leagues: Record<string, League>;
  selectedSeason: number;
  currentSeason: number;
  onUserSwitch: (user: FoundUser) => void;
  isLoading?: boolean;
  leagueDetails: Record<string, { drafts: any, scores: any, eliminationPicks: any }>;
}

interface FoundUser {
  userName: string;
  survivorName: string;
  realName: string;
  email: string;
  leagueId: string;
  paymentMethod: string;
  season: number;
  rank?: number;
  swapUsed?: boolean;
  [key:string]: any;
}

interface League { // This interface is also defined in App.tsx. A shared types file would be better.
  id: string;
  name: string;
  season: number;
  active?: boolean;
  readyToDraft?: boolean;
  lineupDeadlines?: { [episode: string]: string };
  tribes?: { name: string; color: string }[];
}

const Home: React.FC<HomeProps> = ({ userInfo, userEntries, leagues, selectedSeason, currentSeason, onUserSwitch, isLoading = false, leagueDetails }) => {
  const navigate = useNavigate();
  const [castawaysMap, setCastawaysMap] = useState<Record<string, Castaway[]>>({});
  const [selectedPersonFilter, setSelectedPersonFilter] = useState<string>('all');

  const getCastawayPoints = (cid: number, allScores: any, range?: { start?: number, end?: number }) => {
    let total = 0;
    Object.entries(allScores).forEach(([episode, epScores]: [string, any]) => {
      const episodeNum = parseInt(episode.replace('episode', ''), 10);
      if (episodeNum === 1) return; // Episode 1 is non-scoring tracking
      if (range) {
        if (range.start && episodeNum < range.start) return;
        if (range.end && episodeNum > range.end) return;
      }
      const s = epScores[cid] || epScores[String(cid)];
      if (s) {
        total += (s.tribeImmunityWins || 0) * 1;
        total += (s.tribeRewardWins || 0) * 1;
        total += (s.individualRewardWins || 0) * 2;
        total += (s.rightSideOfVote || 0) * 1;
        total += (s.individualImmunityWins || 0);
        total += (s.votesAgainst || 0) * -0.5;
        total += (s.idolObtained || 0) * 4;
        total += (s.advantageObtained || 0) * 2;
        total += (s.playedIdolAdvantage || 0) * 2;
        total += (s.advantageAbandoned || 0) * -2;
        total += (s.idolAbandoned || 0) * -3;
        total += (s.shotInDarkWin || 0) * 4;
        total += (s.coinFlipWin || 0) * 4;
        if (s.finalPlacement === 1) total += 25;
        if (s.finalPlacement === 2) total += 10;
        if (s.finalPlacement === 3) total += 5;
      }
    });
    return total;
  };

  const calculateDetailedUserScore = (userName: string, lid: string) => {
    const details = leagueDetails[lid];
    if (!details) return { score: 0, scoringPlan: [], elimPoints: 0 };

    const { drafts, scores, eliminationPicks } = details;
    const draft = drafts[userName];
    if (!draft) return { score: 0, scoringPlan: [], elimPoints: 0 };

    const { team, originalTeam, swapUsed, swapHistory = [], swapEffectiveEpisode } = draft;
    const scoringPlan: { castawayId: number, range: { start?: number, end?: number } }[] = [];

    const allPlayerIds = new Set<number>(originalTeam || team);
    swapHistory.forEach((s: any) => {
      allPlayerIds.add(s.swappedOutCastawayId);
      allPlayerIds.add(s.swappedInCastawayId);
    });
    if (originalTeam) originalTeam.forEach((id: number) => allPlayerIds.add(id));
    team.forEach((id: number) => allPlayerIds.add(id));

    allPlayerIds.forEach(id => scoringPlan.push({ castawayId: id, range: {} }));

    swapHistory.forEach((swap: any) => {
      const out = scoringPlan.find(p => p.castawayId === swap.swappedOutCastawayId);
      if (out) out.range.end = swap.episodeOfSwap;
      const ins = scoringPlan.find(p => p.castawayId === swap.swappedInCastawayId);
      if (ins) ins.range.start = swap.episodeOfSwap + 1;
    });

    if (swapUsed && originalTeam) {
      const outId = originalTeam.find((id: number) => !team.includes(id));
      const inId = team.find((id: number) => !originalTeam.includes(id));
      if (outId) {
        const p = scoringPlan.find(plan => plan.castawayId === outId);
        if (p) p.range.end = (swapEffectiveEpisode || 0) - 1;
      }
      if (inId) {
        const p = scoringPlan.find(plan => plan.castawayId === inId);
        if (p) p.range.start = swapEffectiveEpisode;
      }
    }

    const rosterSize = Number(leagues[lid]?.season) >= 51 ? 5 : 3;
    const teamScore = rosterSize > 3
      ? scoreDraftByLineup(draft, scores, (castawayId, episodeScores) => getCastawayPoints(castawayId, { episode: episodeScores }), rosterSize)
      : scoringPlan.reduce((total, item) => total + getCastawayPoints(item.castawayId, scores, item.range), 0);

    let elimPoints = 0;
    const picks = eliminationPicks[userName] || {};
    Object.entries(picks).forEach(([ep, pickedId]) => {
      const epScores = scores[ep];
      if (epScores && pickedId) {
        const isVotedOut = Object.keys(epScores).some(cid => epScores[cid].votedOut && Number(cid) === Number(pickedId));
        if (isVotedOut) elimPoints += 3;
      }
    });
    return { score: teamScore + elimPoints, scoringPlan, elimPoints };
  };

  useEffect(() => {
    const fetchCastaways = async () => {
      const uniqueLeagueIds = Array.from(new Set(userEntries.map(e => e.leagueId)));
      const newMap: Record<string, Castaway[]> = {};
      
      await Promise.all(uniqueLeagueIds.map(async (lid) => {
        try {
          const data = await getCastaways(lid);
          newMap[lid] = data;
        } catch (e) {
          console.error(`Failed to load castaways for league ${lid}`, e);
        }
      }));
      setCastawaysMap(newMap);
    };
    if (userEntries.length > 0) fetchCastaways();
  }, [userEntries]);

  const handleSwitchUser = (entry: FoundUser) => {
    onUserSwitch(entry);
    navigate(`/league/${entry.leagueId}`);
  };

  // Current means the latest configured application season, not the latest season the user joined.
  const latestEntrySeason = currentSeason;

  // Extract unique players/identities registered under this account
  const uniquePersons = useMemo(() => {
    const map = new Map<string, { key: string; displayName: string; realName: string; entryCount: number; s51Count: number }>();
    userEntries.forEach(entry => {
      const pName = (entry.realName && entry.realName.trim()) 
        ? entry.realName.trim() 
        : (entry.survivorName || entry.userName);
      const key = pName.toLowerCase();
      const existing = map.get(key);
      const isS51 = Number(entry.season) === latestEntrySeason;
      if (!existing) {
        map.set(key, {
          key,
          displayName: pName,
          realName: entry.realName || '',
          entryCount: 1,
          s51Count: isS51 ? 1 : 0
        });
      } else {
        existing.entryCount += 1;
        if (isS51) existing.s51Count += 1;
      }
    });
    return Array.from(map.values()).sort((a, b) => b.s51Count - a.s51Count || a.displayName.localeCompare(b.displayName));
  }, [userEntries, latestEntrySeason]);

  // Filter userEntries based on selected person filter
  const displayedEntries = useMemo(() => {
    if (selectedPersonFilter === 'all') return userEntries;
    return userEntries.filter(entry => {
      const pName = (entry.realName && entry.realName.trim())
        ? entry.realName.trim().toLowerCase()
        : (entry.survivorName || entry.userName).toLowerCase();
      return pName === selectedPersonFilter;
    });
  }, [userEntries, selectedPersonFilter]);

  const currentEntries = displayedEntries.filter(entry => Number(entry.season) === latestEntrySeason);
  const previousEntries = displayedEntries
    .filter(entry => Number(entry.season) !== latestEntrySeason)
    .sort((a, b) => Number(b.season) - Number(a.season));
  const currentSeasonLeagues = Object.values(leagues).filter(league => Number(league.season) === latestEntrySeason);

  const renderLeagueCard = (entry: FoundUser) => {
    const league = leagues[entry.leagueId];
    const isCurrentSeason = Number(entry.season) === latestEntrySeason;
    
    // Get detailed data
    const details = leagueDetails[entry.leagueId];
    const scores = details?.scores || {};
    const castaways = castawaysMap[entry.leagueId] || [];

    // Recalculate everything locally to match LeagueStandings and ensure accuracy
    const { score: calculatedScore, scoringPlan, elimPoints } = calculateDetailedUserScore(entry.userName, entry.leagueId);
    const allUserScores = Object.keys(details?.drafts || {}).map(u => ({
      userName: u,
      score: calculateDetailedUserScore(u, entry.leagueId).score
    })).sort((a, b) => b.score - a.score);
    const calculatedRank = allUserScores.findIndex(u => u.userName === entry.userName) + 1;

    // Logic for Weekly Elimination Pick button (Until Episode 10)
    // We assume if 'episode10' key exists in scores, episode 10 has happened/started.
    const isBeforeEp10 = !scores['episode10'];
    const isSeason51Plus = Number(entry.season) >= 51;
    const userDraft = details?.drafts?.[entry.userName];
    const hasSubmittedDraft = Boolean(userDraft && Array.isArray(userDraft.team) && userDraft.team.length > 0);

    const nextEpEntry = (Object.entries(league?.lineupDeadlines || {}) as [string, string][])
      .filter(([, deadline]) => new Date(deadline).getTime() > Date.now())
      .sort(([a], [b]) => Number(a) - Number(b))[0];
    const scoredEpisodes = Object.keys(scores).map(k => Number(k.replace('episode', ''))).filter(n => !isNaN(n) && n > 0);
    const maxScoredEp = scoredEpisodes.length > 0 ? Math.max(...scoredEpisodes) : 1;
    const activeEpisodeNum = nextEpEntry ? Number(nextEpEntry[0]) : Math.max(maxScoredEp, 2);
    const activeIds = (isSeason51Plus && userDraft)
      ? activeCastawayIdsForEpisode(userDraft, activeEpisodeNum, 5, scores)
      : (userDraft?.team || []);

    return (
      <Col key={`${entry.leagueId}-${entry.season}-${entry.userName}`} md={6} lg={4} className="mb-3">
        <Card className="h-100 shadow-sm home-card">
          <Card.Body className="d-flex flex-column">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <Card.Title>
                  <Button variant="link" onClick={() => handleSwitchUser(entry)} style={{ textDecoration: 'none', color: 'inherit', padding: 0, fontSize: '1.25rem', textAlign: 'left' }}>
                    {league?.name || `League #${entry.leagueId}`}
                  </Button>
                </Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                  <span className="me-2">Score: <strong>{calculatedScore}</strong></span>
                  <span className="me-2">Rank: <strong>{hasSubmittedDraft && calculatedRank > 0 ? `#${calculatedRank}` : '—'}</strong></span>
                </Card.Subtitle>
                <Card.Subtitle className="mb-2 text-muted">
                  Season {league?.season || entry.season}
                </Card.Subtitle>
              </div>

              {/* Team Breakdown (Images + Points + Active/Bench) */}
              <div className="d-flex flex-column align-items-end" style={{ maxWidth: '55%' }}>
                {scoringPlan.length > 0 ? (
                  <>
                    <div className="d-flex flex-wrap justify-content-end mb-1" style={{ gap: '10px' }}>
                      {scoringPlan.map(item => {
                        const castaway = castaways.find(c => c.id === item.castawayId);
                        if (!castaway) return null;

                        const cp = getCastawayPoints(item.castawayId, scores, item.range);
                        const isVotedOut = Object.values(scores).some((ep: any) => ep[item.castawayId]?.votedOut);
                        const isEvacuated = Object.values(scores).some((ep: any) => ep[item.castawayId]?.involuntaryEvacuation);
                        const isVoluntaryExit = Object.values(scores).some((ep: any) => ep[item.castawayId]?.voluntaryExit);
                        const isOutOfGame = isVoluntaryExit || isVotedOut || isEvacuated;
                        const finalPlacement = Object.values(scores).reduce((acc: number, ep: any) => {
                          const p = ep[item.castawayId]?.finalPlacement;
                          return (p && p > 0) ? p : acc;
                        }, 0);
                        const isActive = !isSeason51Plus || (!isOutOfGame && activeIds.includes(item.castawayId));
                        const tribeColor = getTribeColor(castaway.tribe, (league as any)?.tribes, castaway.tribeColor);

                        return (
                          <div
                            key={castaway.id}
                            className={`position-relative ${isVoluntaryExit ? 'voted-out' : isVotedOut ? 'voted-out' : isEvacuated ? 'evacuated' : ''} ${isSeason51Plus && !isActive && !isOutOfGame ? 'opacity-75' : ''}`}
                            style={{ width: '44px', height: '44px', marginBottom: '8px' }}
                          >
                            <Image
                              src={castaway.imageUrl}
                              roundedCircle
                              thumbnail
                              referrerPolicy="no-referrer"
                              style={{
                                width: '100%',
                                height: '100%',
                                padding: 0,
                                objectFit: 'cover',
                                border: `2.5px solid ${isActive ? tribeColor : '#94a3b8'}`,
                                boxShadow: isActive ? '0 0 6px rgba(16, 185, 129, 0.45)' : 'none'
                              }}
                              title={`${castaway.name} (${castaway.tribe || 'No Tribe'}) - ${isOutOfGame ? (isVoluntaryExit ? '🚪 Voluntary Exit' : isVotedOut ? '🔥 Voted Out' : '🚑 Evacuated') : (isActive ? '🟢 Active 3 (Scoring)' : '⏸️ Bench (Reserve)')}`}
                            />
                            <div style={{ position: 'absolute', top: -3, left: -3, display: 'flex', flexDirection: 'column', zIndex: 10 }}>
                              {finalPlacement === 1 && <Badge bg="warning" text="dark" style={{ border: '1px solid gold', fontSize: '0.5rem', padding: '2px 3px' }}>1st</Badge>}
                              {finalPlacement === 2 && <Badge bg="secondary" style={{ border: '1px solid silver', fontSize: '0.5rem', padding: '2px 3px' }}>2nd</Badge>}
                              {finalPlacement === 3 && <Badge bg="danger" style={{ border: '1px solid #cd7f32', backgroundColor: '#cd7f32', fontSize: '0.5rem', padding: '2px 3px' }}>3rd</Badge>}
                            </div>
                            {isSeason51Plus && !isOutOfGame && (
                              <Badge
                                bg={isActive ? 'success' : 'secondary'}
                                className="position-absolute"
                                style={{
                                  top: -4,
                                  right: -4,
                                  fontSize: '0.5rem',
                                  padding: '1px 3px',
                                  borderRadius: '4px',
                                  zIndex: 10,
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.35)'
                                }}
                              >
                                {isActive ? 'ACT' : 'BN'}
                              </Badge>
                            )}
                            <Badge bg="dark" className="position-absolute" style={{ bottom: -8, left: '50%', transform: 'translateX(-50%)', fontSize: '0.6rem', padding: '2px 4px', border: '1px solid white', zIndex: 11 }}>
                              {cp}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                    {isSeason51Plus && (
                      <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, marginTop: '2px', textAlign: 'right' }}>
                        <span className="text-success">🟢 ACT (Active)</span> &bull; <span>⏸️ BN (Bench)</span>
                      </div>
                    )}
                  </>
                ) : (
                  <Badge bg="warning" text="dark" className="p-2 mb-2 shadow-sm text-center">
                    ⚠️ Roster Not Drafted
                  </Badge>
                )}
                <div style={{ fontSize: '0.72rem', fontWeight: 'bold', color: '#475569', marginTop: '2px' }}>
                  Elim Pick Pts: <span className="text-primary">+{elimPoints}</span>
                </div>
              </div>
            </div>

            <div className="mt-3 mb-2 p-2 rounded bg-light border">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="small text-muted" style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em' }}>
                  PLAYER / ENTRY
                </span>
                <Badge bg="dark" className="px-2 py-1" style={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                  Season {league?.season || entry.season}
                </Badge>
              </div>
              <div className="fw-bold text-dark text-break" style={{ fontSize: '0.95rem', lineHeight: '1.3' }}>
                {entry.survivorName || entry.userName}
              </div>
              {entry.realName && entry.realName !== (entry.survivorName || entry.userName) && (
                <div className="text-muted small mt-1 text-truncate" style={{ fontSize: '0.8rem' }} title={entry.realName}>
                  Real Name: <strong>{entry.realName}</strong>
                </div>
              )}
            </div>

            <div className="d-grid gap-2 mt-auto">
              <Button variant="info" size="sm" onClick={() => {
                onUserSwitch(entry);
                const targetUrl = `/league/${entry.leagueId}/highlights?userName=${entry.userName}`;
                navigate(targetUrl);
              }} className="text-white">
                View Season Highlights
              </Button>
              <Button variant="secondary" size="sm" onClick={() => {
                onUserSwitch(entry);
                navigate(`/league/${entry.leagueId}#${entry.userName}`);
              }}>
                View Current Standings
              </Button>
              <Button variant="primary" size="sm" onClick={() => {
                onUserSwitch(entry);
                navigate('/survivor-scores');
              }}>
                View My Survivor Scores
              </Button>
              {isCurrentSeason && Number(entry.season) < 51 && (
                <Button variant="warning" size="sm" onClick={() => {
                  onUserSwitch(entry);
                  navigate('/swaps');
                }} disabled={entry.swapUsed}>
                {entry.swapUsed ? 'Swap Already Used' : 'Make Player Swap'}
                </Button>
              )}
              {isCurrentSeason && Number(entry.season) >= 51 && (
                <>
                  {hasSubmittedDraft ? (
                    <>
                      <Button variant="success" size="sm" onClick={() => {
                        onUserSwitch(entry);
                        navigate('/lineup');
                      }}>
                        ⚡ Weekly Active Lineup
                      </Button>
                      <Button variant="outline-success" size="sm" onClick={() => {
                        onUserSwitch(entry);
                        navigate('/draft');
                      }}>
                        Draft & Full Roster
                      </Button>
                    </>
                  ) : (
                    <Button variant="primary" size="sm" onClick={() => {
                      onUserSwitch(entry);
                      navigate('/draft');
                    }} className="fw-bold">
                      🎯 Draft Your 5-Player Team
                    </Button>
                  )}
                </>
              )}
              {isCurrentSeason && isBeforeEp10 && <Button variant="dark" size="sm" onClick={() => {
                onUserSwitch(entry);
                navigate(`/leagues/${entry.leagueId}/elimination-pick`);
              }}>Weekly Elimination Pick</Button>}
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const renderUpcomingLeague = (league: League) => {
    return (
      <Col key={league.id} md={6} lg={4} className="mb-3">
        <Card className="h-100 shadow-sm home-card border-warning" style={{ background: '#ffffff' }}>
          <Card.Body className="d-flex flex-column">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <Card.Title className="mb-1">{league.name}</Card.Title>
                <Card.Subtitle className="text-muted">Season {league.season}</Card.Subtitle>
              </div>
              <Badge bg="warning" text="dark" className="px-2 py-1">
                Not Joined Yet
              </Badge>
            </div>
            
            <Alert variant="warning" className="py-2 px-3 small my-2 border-0" style={{ backgroundColor: 'rgba(245, 158, 11, 0.12)' }}>
              <strong>🌴 Join this league</strong> to create your team and draft your 5-player roster for Season {league.season}!
            </Alert>

            <div className="d-grid gap-2 mt-auto pt-2">
              <Button 
                variant="primary" 
                size="sm" 
                onClick={() => {
                  navigate(`/join?season=${league.season}&leagueId=${encodeURIComponent(league.id)}`);
                }}
                className="fw-bold"
              >
                🏝️ Sign Up / Join Season {league.season} League &rarr;
              </Button>
              <Button 
                variant="outline-success" 
                size="sm" 
                onClick={() => navigate(`/mock-draft?season=${league.season}&leagueId=${encodeURIComponent(league.id)}`)}
              >
                🎯 Practice Mock Draft
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  // Distinct real names across all entries linked to this email
  const distinctFirstNames = Array.from(new Set(
    userEntries
      .map(e => e.realName ? e.realName.trim().split(' ')[0] : '')
      .filter(Boolean)
  ));
  
  const primaryAccountName = userInfo.realName 
    ? userInfo.realName.split(' ')[0] 
    : (userInfo.survivorName || userInfo.userName);

  const welcomeGreeting = distinctFirstNames.length === 1
    ? distinctFirstNames[0]
    : distinctFirstNames.length === 2
    ? `${distinctFirstNames[0]} & ${distinctFirstNames[1]}`
    : distinctFirstNames.length > 2
    ? `${primaryAccountName} & Family`
    : primaryAccountName;

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <div>
          <h2 className="mb-1 fw-bold">Welcome, {welcomeGreeting}!</h2>
          <div className="text-muted small d-flex align-items-center gap-2 flex-wrap">
            <span>Account: <strong>{userInfo.email || userInfo.userName}</strong></span>
            {userEntries.length > 1 && (
              <Badge bg="secondary" className="px-2 py-1">
                {userEntries.length} entries registered across seasons
              </Badge>
            )}
          </div>
        </div>

        {/* Multi-Player Filter / Switcher for shared email accounts */}
        {uniquePersons.length > 1 && (
          <div className="p-2 rounded bg-white border shadow-sm d-flex align-items-center gap-2 flex-wrap">
            <span className="small fw-bold text-muted ps-1">👤 Filter by Player:</span>
            <div className="d-flex gap-1 flex-wrap">
              <Button
                size="sm"
                variant={selectedPersonFilter === 'all' ? 'primary' : 'outline-secondary'}
                className="rounded-pill px-3 py-1"
                onClick={() => setSelectedPersonFilter('all')}
                style={{ fontSize: '0.82rem' }}
              >
                All Players ({userEntries.length})
              </Button>
              {uniquePersons.map(person => (
                <Button
                  key={person.key}
                  size="sm"
                  variant={selectedPersonFilter === person.key ? 'primary' : 'outline-secondary'}
                  className="rounded-pill px-3 py-1"
                  onClick={() => setSelectedPersonFilter(person.key)}
                  style={{ fontSize: '0.82rem' }}
                >
                  {person.displayName} ({person.entryCount})
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
            <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          </div>
        ) : (
          <>
            <h4 className="mb-3 border-bottom pb-2">Current Season (Season {latestEntrySeason})</h4>
            <Row>
              {currentEntries.map(renderLeagueCard)}
              {currentSeasonLeagues.filter(league => !currentEntries.some(entry => entry.leagueId === league.id)).map(renderUpcomingLeague)}
              {currentEntries.length === 0 && currentSeasonLeagues.length === 0 && <Col><Alert variant="secondary">No leagues for Season {latestEntrySeason}</Alert></Col>}
            </Row>

            {userEntries.length === 0 && (
              <Alert variant="info">
                You haven't joined any leagues for Season {latestEntrySeason}. <Link to="/join">Join a League</Link>.
              </Alert>
            )}

            {previousEntries.length > 0 && (
              <div className="mt-4">
                <h4 className="mb-3 border-bottom pb-2">Previous Seasons</h4>
                <Row>{previousEntries.map(renderLeagueCard)}</Row>
              </div>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default Home;